// src/hooks/usePendingApprovalsCount.js
import { useState, useEffect, useCallback, useRef } from 'react';
import { assetService }            from '../services/assetService';
import { preferencesService }      from '../services/preferencesService';
import { singleValueListService }  from '../services/singleValueListService';

/**
 * Reads the asset's approverList and returns the highest level whose
 * status contains the word "approved". Returns 0 for a fresh asset.
 * Also treats "rejected" rows as level 0 (back to Created).
 */
const deriveCurrentLevel = (asset) => {
  const list = Array.isArray(asset?.approverList) ? asset.approverList : [];
  for (const row of list) {
    if (String(row?.status || '').toLowerCase().includes('rejected')) {
      return 0; // rejected → reset to Created
    }
  }
  let approvedUpTo = 0;
  for (const row of list) {
    const s = String(row?.status || '').toLowerCase();
    if (s.includes('approved')) {
      approvedUpTo = Math.max(approvedUpTo, Number(row.level) || 0);
    }
  }
  return approvedUpTo;
};

const hasRejection = (asset) =>
  (Array.isArray(asset?.approverList) ? asset.approverList : []).some((r) =>
    String(r?.status || '').toLowerCase().includes('rejected'),
  );

export const usePendingApprovalsCount = (currentUser, routeKey = '') => {
  const [count, setCount]                   = useState(0);
  const [userIsApprover, setUserIsApprover] = useState(false);
  const cancelledRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const user = currentUser || (() => {
        try {
          const raw = localStorage.getItem('userData');
          return raw ? JSON.parse(raw) : null;
        } catch { return null; }
      })();

      if (!user?.companyId || !user?.email) {
        setCount(0);
        setUserIsApprover(false);
        return;
      }

      const enabled = await preferencesService.isApproverEnabled(user);
      if (!enabled) {
        setCount(0);
        setUserIsApprover(false);
        return;
      }

      const items = await singleValueListService.getApproverItems(user.companyId);
      const email = String(user.email).toLowerCase();

      // SVL: { level → Set<email> }
      const usersByLevel = new Map();
      for (const row of items || []) {
        const lvl = Number(row.level) || 1;
        if (!usersByLevel.has(lvl)) usersByLevel.set(lvl, new Set());
        const bucket = usersByLevel.get(lvl);
        for (const e of row.users || []) bucket.add(String(e).toLowerCase());
      }

      const myLevels = new Set(
        Array.from(usersByLevel.entries())
          .filter(([, s]) => s.has(email))
          .map(([lvl]) => lvl),
      );

      setUserIsApprover(myLevels.size > 0);
      if (myLevels.size === 0) {
        setCount(0);
        return;
      }

      const maxLevel = Math.max(0, ...Array.from(usersByLevel.keys()));

      const all = await assetService.fetchAssets({ companyId: user.companyId });

      const pending = (all || []).filter((a) => {
        if (hasRejection(a)) return false;                   // rejected → Created
        const lvl = deriveCurrentLevel(a);
        if (maxLevel > 0 && lvl >= maxLevel) return false;   // fully approved

        const nextLevel = lvl + 1;
        if (!myLevels.has(nextLevel)) return false;

        // Authoritative source: the asset's own approverList written by
        // CreateEditAssetModal (or updated by a prior approval action).
        const row = (Array.isArray(a.approverList) ? a.approverList : []).find(
          (r) => Number(r.level) === nextLevel,
        );
        if (!row?.email) return false;
        return String(row.email).toLowerCase() === email;
      });

      if (!cancelledRef.current) setCount(pending.length);
    } catch (err) {
      console.warn('[usePendingApprovalsCount] failed:', err);
      if (!cancelledRef.current) {
        setCount(0);
        setUserIsApprover(false);
      }
    }
  }, [currentUser]);

  useEffect(() => {
    cancelledRef.current = false;
    load();
    const interval = setInterval(load, 60000);

    const onUpdate = () => load();
    window.addEventListener('approvals-updated', onUpdate);

    return () => {
      cancelledRef.current = true;
      clearInterval(interval);
      window.removeEventListener('approvals-updated', onUpdate);
    };
  }, [load, routeKey]);

  return { count, userIsApprover, reload: load };
};

export default usePendingApprovalsCount;